import React from 'react';
import { CaretLeft, CaretRight, LockKey, Tray, WarningCircle } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import {
  Avatar,
  Button,
  Empty,
  Label,
  Modal,
  Pill,
  Skeleton,
  Textarea,
} from '../../components/desktop';
import { getStatusColor, getStatusLabel } from '../../../utils/orderStatus';
import { errorText, LOAD_FAILURE } from '../../../utils/errorText';

import './console.css';

/**
 * The console pattern. Six admin routes share this shape (B8), and so will
 * every later management page: a page header, a toolbar, a TABLE, pagination,
 * and a right drawer holding one record with an approve/reject pair.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Before the conversion the six routes carried THREE separate `Pagination.jsx`
 * files, two of them identical but for a `2` suffix on every class name to dodge
 * a global collision, TWO unrelated `StatusBadge` components, four hand-rolled
 * table skeletons and four hand-rolled `<table>`s. The library already ships
 * `Table`, `Pagination`, `Pill`, `Skeleton`, `Empty` and `Spinner`; what it does
 * NOT ship is the small set of arrangements between them that these six pages
 * genuinely share. Those, and only those, live here.
 *
 * Everything in this file is presentational. No fetch, no navigate, no toast,
 * no timers. The containers own all of that.
 *
 * THE ONE RULE THAT KEEPS BEING BROKEN AND IS OBEYED HERE
 * ------------------------------------------------------
 * `.dsk-btn--danger` DOES NOT EXIST. A destructive command is a GHOST button
 * whose label carries the consequence ("Reject application", not "Reject"),
 * plus a real confirmation that does the work. `ConfirmDialog` is that
 * confirmation, and it never tints its confirm button either: the sentence
 * above it is what states the stakes.
 */

/* ------------------------------------------------------------------ status */

/**
 * StatusPill. The single replacement for both dead `StatusBadge` components and
 * the third one still shared with B6.
 *
 * The tone is read from `utils/orderStatus.js`, which is the only place in the
 * app that knows the real vocabulary, and it returns FOUR buckets plus one:
 * `warning`, `info`, `success`, `error`, and `primary` on `approved` alone.
 * `Pill` accepts those names verbatim, so nothing is translated here and there
 * is no second mapping table to drift.
 *
 * "Shipped" is not a status value in this app and must never be introduced.
 * Anything unrecognised falls through to `getStatusLabel`'s snake-case-to-Title
 * fallback and the default (untinted) pill, which is honest: an unknown status
 * gets no colour opinion.
 */
export function StatusPill({ status, label, className, ...rest }) {
  if (!status) return null;
  const tone = getStatusColor(status);
  return (
    <Pill tone={tone === 'default' ? 'default' : tone} className={className} {...rest}>
      {label || getStatusLabel(status)}
    </Pill>
  );
}

/* -------------------------------------------------------------------- cell */

/**
 * ConsoleCell, the two-line identity cell that leads every one of these tables:
 * an avatar, the thing's name, and the corroborating line under it.
 *
 * Both lines always render: rows are the comfortable 48px everywhere now, and
 * the compact 40px row that could not hold a 32px avatar over two lines is
 * gone along with the density control. See `Table.jsx`.
 */
export function ConsoleCell({ title, sub, initials, avatar = true, className }) {
  return (
    <div className={cn('dsa-console__cell', className)}>
      {avatar ? <Avatar initials={initials} name={typeof title === 'string' ? title : undefined} /> : null}
      <div className="dsa-console__cell-text">
        <div className="dsa-console__cell-title">{title}</div>
        {sub ? <div className="dsa-console__cell-sub">{sub}</div> : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ detail lists */

/** DetailList, the label/value grid in a drawer body. Renders a real `<dl>`. */
export function DetailList({ className, ...rest }) {
  return <dl className={cn('dsa-console__dl', className)} {...rest} />;
}

/**
 * One row of it. A row with no value is dropped entirely rather than rendered
 * as an empty hairline: a missing fact should not cost a line of the drawer.
 */
export function DetailRow({ label, value, keepEmpty = false }) {
  if (!keepEmpty && (value === undefined || value === null || value === '')) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * A titled block inside a drawer body. `.dsk-drawer__section` supplies the
 * quiet uppercase label; this supplies the rhythm between consecutive blocks.
 */
export function DetailSection({ title, children, className, ...rest }) {
  return (
    <section className={cn('dsa-console__section', className)} {...rest}>
      {title ? <p className="dsk-drawer__section">{title}</p> : null}
      {children}
    </section>
  );
}

/* ------------------------------------------------------- reason and notice */

/**
 * ReasonBlock, a rejection reason. Border and words, never a filled red panel:
 * the sentence carries the meaning and the colour only corroborates it.
 */
export function ReasonBlock({ label = 'Rejection reason', children, className }) {
  if (!children) return null;
  return (
    <div className={cn('dsa-console__reason', className)}>
      <strong className="dsa-console__reason-label">{label}</strong>
      {children}
    </div>
  );
}

/**
 * Notice, the honest label on a screen that is not reading real data yet, or on
 * a record missing something the next step needs.
 *
 * This exists because the app shipped a whole admin route rendering a hardcoded
 * array as though it were live. A screen in that state is allowed to exist; it
 * is not allowed to be silent about it.
 */
export function Notice({ title, children, className }) {
  return (
    <p className={cn('dsa-console__notice', className)}>
      <Icon as={WarningCircle} size="sm" />
      <span>
        {title ? <strong className="dsa-console__notice-title">{title}</strong> : null}
        {children}
      </span>
    </p>
  );
}

/* ------------------------------------------------------------------ layout */

/** The vertical stack a console page is: header, bands, table. */
export function ConsoleStack({ className, ...rest }) {
  return <div className={cn('dsa-console__stack', className)} {...rest} />;
}

/** The auto-fitting stat grid. `.dsk-stat-row` is fixed at four tracks. */
export function StatGrid({ className, ...rest }) {
  return <div className={cn('dsa-console__stats', className)} {...rest} />;
}

/** The drawer footer pair, or any right-aligned command row. */
export function ActionRow({ start = false, className, ...rest }) {
  return (
    <div
      className={cn('dsa-console__actions', start && 'dsa-console__actions--start', className)}
      {...rest}
    />
  );
}

/** What replaces the action pair once the decision has been made. */
export function ResolvedNote({ className, ...rest }) {
  return <p className={cn('dsa-console__resolved', className)} {...rest} />;
}

/* ------------------------------------------------------------------ states */

/**
 * The three states every one of these tables can be in besides "rows".
 * `Empty`'s four tones exist precisely because the way OUT differs: create,
 * clear the filter, retry, or nothing (you may not see this).
 *
 * THESE THREE CARRY THE ILLUSTRATION FOR ROUGHLY FORTY OF THE APP'S SIXTY-THREE
 * EMPTY-STATE INSTANCES. A console table's empty state owns the whole table, so
 * by `illustrations/README.md` it is the illustration treatment, not the glyph.
 *
 * `inline` was dropped from all three to get there, and that is a real change
 * rather than an oversight. `inline` means "you are inside something that
 * already has an inset, do not add a second one", and `Empty` reads it as a
 * signal that the state is a small one sitting quietly inside a panel, which is
 * why it suppresses the artwork. A console table body is the opposite: it is
 * the whole surface. The `icon` props stay because they cost nothing and are
 * the right answer again the moment one of these is reused somewhere tight.
 */
export function ConsoleEmpty({
  title = 'Nothing here yet',
  text,
  /**
   * The filtered case says something DIFFERENT, and it used to say the wrong
   * thing: the title flipped to "No matches" while `text` stayed whatever the
   * caller wrote for the unfiltered case, so ten tables read "No matches" over
   * "Applications appear here as sellers complete registration." The two are
   * separate props now and the unfiltered sentence can never leak into the
   * filtered state.
   */
  filteredText,
  icon = Tray,
  /**
   * Scenario slug for the unfiltered case. `no-orders-yet` is the default
   * because most of these tables are ledgers that fill as other people act.
   * The filtered case is always `filters-matched-nothing` and is not a caller's
   * choice: what happened is the same whichever table it happened in.
   */
  illustration = 'no-orders-yet',
  filtered = false,
  onClear,
  /** Optional way OUT of an empty table. Only ever a route that exists. */
  action,
  /**
   * Opt back into the GLYPH. A few of these render inside a dashboard `Card`
   * rather than under a full table, and a 112px illustration in a panel that
   * size dominates the panel it is meant to sit quietly inside. `Empty` reads
   * `inline` as exactly that signal and suppresses the artwork.
   */
  inline = false,
}) {
  if (filtered) {
    return (
      <Empty
        tone="filtered"
        inline={inline}
        icon={icon}
        illustration="filters-matched-nothing"
        title="No matches"
        text={filteredText || 'No records match the filters you applied.'}
        actions={
          onClear ? (
            <Button variant="ghost" size="sm" onClick={onClear}>
              Clear filters
            </Button>
          ) : null
        }
      />
    );
  }
  return (
    <Empty
      inline={inline}
      icon={icon}
      illustration={illustration}
      title={title}
      text={text}
      actions={action || null}
    />
  );
}

export function ConsoleError({ error, onRetry, title = 'Could not load this', inline = false }) {
  return (
    <Empty
      tone="error"
      inline={inline}
      icon={WarningCircle}
      illustration="request-failed"
      title={title}
      text={errorText(error, LOAD_FAILURE)}
      actions={
        onRetry ? (
          <Button variant="ghost" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ) : null
      }
    />
  );
}

/**
 * The permission wall. Ink, not red: this is the system holding a line, not a
 * fault, and there is no way out, so no action is offered.
 */
export function ConsoleDenied({
  title = 'You do not have access to this',
  text = 'This console is limited to administrator accounts. Ask an administrator to grant you access.',
}) {
  return <Empty tone="locked" icon={LockKey} illustration="access-denied" title={title} text={text} />;
}

/* ----------------------------------------------------------------- gallery */

/**
 * The listing images in the marketplace detail drawer. One at a time, prev and
 * next as real focusable icon buttons, and a count so the position is stated
 * rather than only implied by a carousel.
 */
export function DetailGallery({ images = [], index = 0, onPrev, onNext, alt = '' }) {
  if (!images.length) {
    return (
      <div className="dsa-console__gallery">
        <span className="dsa-console__gallery-empty">No images supplied</span>
      </div>
    );
  }

  const safeIndex = Math.min(Math.max(index, 0), images.length - 1);

  return (
    <div className="dsa-console__gallery">
      <img src={images[safeIndex]?.image} alt={alt} />
      {images.length > 1 ? (
        <>
          <span className="dsa-console__gallery-nav dsa-console__gallery-nav--prev">
            <button type="button" className="dsk-iconbtn" onClick={onPrev} aria-label="Previous image">
              <Icon as={CaretLeft} size="sm" />
            </button>
          </span>
          <span className="dsa-console__gallery-nav dsa-console__gallery-nav--next">
            <button type="button" className="dsk-iconbtn" onClick={onNext} aria-label="Next image">
              <Icon as={CaretRight} size="sm" />
            </button>
          </span>
          <span className="dsa-console__gallery-count">
            {safeIndex + 1} of {images.length}
          </span>
        </>
      ) : null}
    </div>
  );
}

/** The small thumbnail strip in a table cell. Capped, so a row cannot grow. */
export function ThumbStrip({ images = [], max = 3, alt = '' }) {
  if (!images.length) return null;
  const shown = images.slice(0, max);
  return (
    <span className="dsa-console__thumbs">
      {shown.map((img) => (
        <img key={img.id ?? img.image} src={img.image} alt={alt} />
      ))}
      {images.length > max ? <span className="dsk-hint">+{images.length - max}</span> : null}
    </span>
  );
}

/* ------------------------------------------------------- confirm and forms */

/**
 * ConfirmDialog, the thing that makes a ghost destructive button safe.
 *
 * Both buttons are library buttons and NEITHER is tinted. The consequence lives
 * in the sentence and in the confirm label, which is why `confirmLabel` is a
 * required-in-spirit prop and defaults to something specific rather than "OK".
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  busyLabel = 'Working',
  children,
}) {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="dsa-console__confirm">{children}</div>
      <ActionRow style={{ marginTop: 'var(--ds-space-6)' }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={busy}>
          {busy ? busyLabel : confirmLabel}
        </Button>
      </ActionRow>
    </Modal>
  );
}

/**
 * RejectReasonForm, the rejection-reason body.
 *
 * The legacy `components/UI/RejectionForm/form.jsx` this replaces on these
 * routes emitted a `GradientButton`, which is being removed rather than
 * restyled. Its two-path behaviour is preserved exactly: Skip submits with no
 * reason, Submit sends the typed one. Reject is destructive, so the submit
 * label says what it does.
 */
export function RejectReasonForm({
  open,
  onClose,
  onSubmit,
  title = 'Reason for rejection',
  submitLabel = 'Reject',
  initialReason = '',
  busy = false,
  hint = 'The seller sees this. Say what has to change for a resubmission to pass.',
}) {
  const [reason, setReason] = React.useState(initialReason);
  const fieldId = `${React.useId()}-reason`;

  // A fresh open starts from the record's own stored reason, not from whatever
  // was typed against the previous record.
  React.useEffect(() => {
    if (open) setReason(initialReason || '');
  }, [open, initialReason]);

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form
        className="dsa-console__form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(reason);
        }}
      >
        <div>
          <Label htmlFor={fieldId}>Reason</Label>
          <Textarea
            id={fieldId}
            rows={5}
            value={reason}
            placeholder="Explain what is wrong and what would make this pass."
            onChange={(event) => setReason(event.target.value)}
            disabled={busy}
          />
          <p className="dsk-hint">{hint}</p>
        </div>
        <ActionRow>
          <Button variant="ghost" onClick={() => onSubmit(undefined)} disabled={busy}>
            Skip reason
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Working' : submitLabel}
          </Button>
        </ActionRow>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------- stat tiles */

/**
 * A stat whose value may still be arriving. `Skeleton` mirrors the shape of the
 * number rather than spinning over a layout we can already describe.
 */
export function StatValue({ loading, children }) {
  if (loading) return <Skeleton shape="title" width="60%" />;
  return children;
}

export default StatusPill;
